import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  User, Mail, Phone, Building2, Calendar, Clock, Shield, Key, Wifi, WifiOff,
  CreditCard, ArrowUpCircle, ArrowDownCircle, Package, BadgeDollarSign,
  Globe, MonitorSmartphone, ShieldAlert, History, BarChart3, RefreshCw,
  ChevronDown, Check, X, AlertTriangle, Wallet, TrendingUp, TrendingDown,
  Activity, Search, Loader2, Copy, ExternalLink, Bell, BellOff, Eye, EyeOff,
  KeyRound, Plus, Trash2, Edit3, Fingerprint
} from 'lucide-react';

const API_BASE = '/api/v1/admin';

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
});

// ─── Format Helpers ──────────────────────────────────────────────────────────

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtMoney = (v, currency = 'INR') => {
  if (v == null) return '—';
  return `${currency === 'INR' ? '₹' : '$'}${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtRate = (v, currency = 'INR') => {
  if (v == null) return '—';
  const num = Number(v);
  const str = num.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
  const decimals = str.includes('.') ? str.split('.')[1].length : 0;
  const minDec = Math.max(decimals, 4);
  return `${currency === 'INR' ? '₹' : '$'}${num.toLocaleString('en-IN', { minimumFractionDigits: minDec, maximumFractionDigits: 6 })}`;
};

const fmtNumber = (v) => {
  if (v == null) return '0';
  return Number(v).toLocaleString('en-IN');
};

const StatusBadge = ({ active, trueLabel = 'Active', falseLabel = 'Inactive' }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
    active ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
  }`}>
    <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-emerald-500' : 'bg-red-500'}`} />
    {active ? trueLabel : falseLabel}
  </span>
);

// ─── Section Card ────────────────────────────────────────────────────────────

const SectionCard = ({ title, icon: Icon, children, action, className = '' }) => (
  <div className={`bg-white rounded-xl border border-gray-100 shadow-sm ${className}`}>
    <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-50">
      <div className="flex items-center gap-2.5">
        {Icon && <Icon size={17} className="text-indigo-500" />}
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      </div>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const InfoRow = ({ label, value, mono = false }) => (
  <div className="flex items-start justify-between py-1.5 gap-4">
    <span className="text-xs text-gray-500 whitespace-nowrap">{label}</span>
    <span className={`text-xs text-gray-800 text-right ${mono ? 'font-mono' : 'font-medium'}`}>
      {value || '—'}
    </span>
  </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const UserCRMDashboard = () => {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [crmData, setCrmData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);
  const searchInputRef = useRef(null);
  const crmDropdownRef = useRef(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showRateModal, setShowRateModal] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);

  // Block Keywords/Mobiles management state
  const [kwSearch, setKwSearch] = useState('');
  const [mbSearch, setMbSearch] = useState('');
  const [showAddKwModal, setShowAddKwModal] = useState(false);
  const [showAddMbModal, setShowAddMbModal] = useState(false);
  const [editKw, setEditKw] = useState(null);
  const [editMb, setEditMb] = useState(null);
  const [newKwText, setNewKwText] = useState('');
  const [newMbText, setNewMbText] = useState('');
  const [newMbReason, setNewMbReason] = useState('');

  // F4 keyboard shortcut to focus search box
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === 'F4') {
        e.preventDefault();
        setDropdownOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 50);
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // Load user list
  useEffect(() => {
    setUsersLoading(true);
    fetch(`${API_BASE}/user-crm/list`, { headers: getHeaders() })
      .then(r => r.json())
      .then(data => { setUsers(Array.isArray(data) ? data : []); setUsersLoading(false); })
      .catch(() => setUsersLoading(false));
  }, []);

  // Load CRM data when user selected
  const loadCRM = useCallback((userId) => {
    if (!userId) return;
    setLoading(true);
    setCrmData(null);
    fetch(`${API_BASE}/user-crm/${userId}`, { headers: getHeaders() })
      .then(r => r.json())
      .then(data => { setCrmData(data); setLoading(false); })
      .catch(() => { setLoading(false); showToast('Failed to load user data', 'error'); });
  }, []);

  const selectUser = (userId) => {
    setSelectedUserId(userId);
    setDropdownOpen(false);
    setSearchTerm('');
    loadCRM(userId);
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Filter users for dropdown
  const filteredUsers = users.filter(u =>
    !searchTerm || u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.full_name && u.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (u.email && u.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Reset highlighted index when search changes
  useEffect(() => { setHighlightedIdx(-1); }, [searchTerm]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIdx >= 0 && crmDropdownRef.current) {
      const items = crmDropdownRef.current.querySelectorAll('[data-crm-user]');
      if (items[highlightedIdx]) items[highlightedIdx].scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIdx]);

  // Keyboard navigation for user dropdown
  const handleCrmSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIdx(prev => Math.min(prev + 1, filteredUsers.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIdx(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightedIdx >= 0 && highlightedIdx < filteredUsers.length) {
      e.preventDefault();
      selectUser(filteredUsers[highlightedIdx].id);
    } else if (e.key === 'Escape') {
      setDropdownOpen(false);
    }
  };

  const selectedUser = users.find(u => u.id === selectedUserId);
  const p = crmData?.profile;
  const plan = crmData?.plan;
  const rates = crmData?.rates;
  const bal = crmData?.balance;
  const recharges = crmData?.recent_recharges;
  const alerts = crmData?.login_alerts;
  const ipHistory = crmData?.ip_history;
  const smsUsage = crmData?.sms_usage;
  const availablePlans = crmData?.available_plans;
  const entities = crmData?.entities;
  const ipWhitelist = crmData?.ip_whitelist;
  const blockKeywords = crmData?.block_keywords;
  const blockMobiles = crmData?.block_mobiles;

  // ─── Action Handlers ────────────────────────────────────────────────────────

  const handleAssignPlan = async (planId) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/user-crm/${selectedUserId}/assign-plan`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify({ plan_id: planId }),
      });
      const data = await res.json();
      if (res.ok) {
        showToast(`Plan "${data.plan_name}" assigned successfully`);
        setShowPlanModal(false);
        loadCRM(selectedUserId);
      } else {
        showToast(data.error || 'Failed to assign plan', 'error');
      }
    } catch { showToast('Network error', 'error'); }
    setActionLoading(false);
  };

  const handleAssignRate = async (pricePerSms, routeType, currency) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/user-crm/${selectedUserId}/assign-rate`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ price_per_sms: parseFloat(pricePerSms), route_type: routeType, currency }),
      });
      const data = await res.json();
      if (res.ok) {
        showToast('Rate assigned successfully');
        setShowRateModal(false);
        loadCRM(selectedUserId);
      } else {
        showToast(data.error || 'Failed to assign rate', 'error');
      }
    } catch { showToast('Network error', 'error'); }
    setActionLoading(false);
  };

  const handleCreditDebit = async (type, amount, reason) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/user-crm/${selectedUserId}/balance`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ type, amount: parseFloat(amount), reason }),
      });
      const data = await res.json();
      if (res.ok) {
        showToast(`${type === 'credit' ? 'Credited' : 'Debited'} ${fmtMoney(amount)} successfully`);
        setShowBalanceModal(false);
        loadCRM(selectedUserId);
      } else {
        showToast(data.error || 'Failed', 'error');
      }
    } catch { showToast('Network error', 'error'); }
    setActionLoading(false);
  };

  // ─── Block Keywords/Mobiles CRUD ────────────────────────────────────────────

  const handleAddKeyword = async () => {
    if (!newKwText.trim() || !selectedUserId) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/global-settings/keyword-block`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ keywords: newKwText, scope: 'USER', user_id: selectedUserId, is_active: true }),
      });
      if (res.ok) {
        showToast('Keyword(s) added');
        setShowAddKwModal(false); setNewKwText('');
        loadCRM(selectedUserId);
      } else {
        const d = await res.json();
        showToast(d.error || 'Failed', 'error');
      }
    } catch { showToast('Network error', 'error'); }
    setActionLoading(false);
  };

  const handleUpdateKeyword = async () => {
    if (!editKw || !newKwText.trim()) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/global-settings/keyword-block/${editKw.id}`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({ keyword: newKwText, scope: editKw.scope, user_id: editKw.scope === 'USER' ? selectedUserId : null, is_active: editKw.is_active }),
      });
      if (res.ok) {
        showToast('Keyword updated');
        setEditKw(null); setNewKwText('');
        loadCRM(selectedUserId);
      } else {
        const d = await res.json();
        showToast(d.error || 'Failed', 'error');
      }
    } catch { showToast('Network error', 'error'); }
    setActionLoading(false);
  };

  const handleDeleteKeyword = async (id) => {
    if (!window.confirm('Deactivate this keyword?')) return;
    try {
      const res = await fetch(`${API_BASE}/global-settings/keyword-block/${id}`, { method: 'DELETE', headers: getHeaders() });
      if (res.ok) { showToast('Keyword deactivated'); loadCRM(selectedUserId); }
      else showToast('Failed', 'error');
    } catch { showToast('Network error', 'error'); }
  };

  const handleAddMobile = async () => {
    if (!newMbText.trim() || !selectedUserId) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/global-settings/mobile-block`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ mobile_numbers: newMbText, scope: 'USER', user_id: selectedUserId, reason: newMbReason, is_active: true }),
      });
      if (res.ok) {
        showToast('Mobile(s) blocked');
        setShowAddMbModal(false); setNewMbText(''); setNewMbReason('');
        loadCRM(selectedUserId);
      } else {
        const d = await res.json();
        showToast(d.error || 'Failed', 'error');
      }
    } catch { showToast('Network error', 'error'); }
    setActionLoading(false);
  };

  const handleUpdateMobile = async () => {
    if (!editMb || !newMbText.trim()) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/global-settings/mobile-block/${editMb.id}`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({ mobile_number: newMbText, scope: editMb.scope, user_id: editMb.scope === 'USER' ? selectedUserId : null, reason: newMbReason, is_active: editMb.is_active }),
      });
      if (res.ok) {
        showToast('Mobile updated');
        setEditMb(null); setNewMbText(''); setNewMbReason('');
        loadCRM(selectedUserId);
      } else {
        const d = await res.json();
        showToast(d.error || 'Failed', 'error');
      }
    } catch { showToast('Network error', 'error'); }
    setActionLoading(false);
  };

  const handleDeleteMobile = async (id) => {
    if (!window.confirm('Deactivate this blocked mobile?')) return;
    try {
      const res = await fetch(`${API_BASE}/global-settings/mobile-block/${id}`, { method: 'DELETE', headers: getHeaders() });
      if (res.ok) { showToast('Mobile unblocked'); loadCRM(selectedUserId); }
      else showToast('Failed', 'error');
    } catch { showToast('Network error', 'error'); }
  };

  // ─── SMS Usage Chart (simple bar chart) ─────────────────────────────────────

  const SMSUsageChart = ({ data }) => {
    if (!data || data.length === 0) return <p className="text-xs text-gray-400 text-center py-6">No SMS usage data available</p>;
    const reversed = [...data].reverse();
    const maxCount = Math.max(...reversed.map(d => d.sms_count || 0), 1);

    return (
      <div className="space-y-3">
        {reversed.map((m, i) => {
          const total = m.sms_count || 0;
          const delivered = m.delivered || 0;
          const failed = m.failed || 0;
          const pending = m.pending || 0;
          const pct = (total / maxCount) * 100;
          return (
            <div key={i} className="group">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-600">{m.month}</span>
                <span className="text-xs font-semibold text-gray-800">{fmtNumber(total)} SMS</span>
              </div>
              <div className="h-7 bg-gray-50 rounded-lg overflow-hidden flex" style={{ width: '100%' }}>
                {total > 0 && (
                  <>
                    <div className="h-full bg-emerald-400 transition-all" style={{ width: `${(delivered / total) * pct}%` }} title={`Delivered: ${fmtNumber(delivered)}`} />
                    <div className="h-full bg-red-400 transition-all" style={{ width: `${(failed / total) * pct}%` }} title={`Failed: ${fmtNumber(failed)}`} />
                    <div className="h-full bg-amber-300 transition-all" style={{ width: `${(pending / total) * pct}%` }} title={`Pending: ${fmtNumber(pending)}`} />
                    <div className="h-full bg-indigo-300 transition-all" style={{ width: `${((total - delivered - failed - pending) / total) * pct}%` }} title={`Other: ${fmtNumber(total - delivered - failed - pending)}`} />
                  </>
                )}
              </div>
              <div className="flex gap-3 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-[10px] text-emerald-600">Delivered: {fmtNumber(delivered)}</span>
                <span className="text-[10px] text-red-600">Failed: {fmtNumber(failed)}</span>
                <span className="text-[10px] text-amber-600">Pending: {fmtNumber(pending)}</span>
              </div>
            </div>
          );
        })}
        <div className="flex items-center gap-4 pt-2 border-t border-gray-100">
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded bg-emerald-400" /><span className="text-[10px] text-gray-500">Delivered</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded bg-red-400" /><span className="text-[10px] text-gray-500">Failed</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded bg-amber-300" /><span className="text-[10px] text-gray-500">Pending</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded bg-indigo-300" /><span className="text-[10px] text-gray-500">Other</span></div>
        </div>
      </div>
    );
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 transition-all ${
          toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'
        }`}>
          {toast.type === 'error' ? <X size={16} /> : <Check size={16} />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <User size={22} className="text-indigo-500" />
          User CRM Dashboard
        </h1>
        <p className="text-sm text-gray-500 mt-1">Advanced user management — all data loaded from database</p>
      </div>

      {/* User Selector */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[300px]">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Select User</label>
            <div className="relative">
              <button
                onClick={() => { setDropdownOpen(!dropdownOpen); setTimeout(() => searchInputRef.current?.focus(), 50); }}
                className="w-full flex items-center justify-between px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm hover:border-indigo-300 transition-colors"
              >
                {selectedUser ? (
                  <span className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">
                      {selectedUser.username.charAt(0).toUpperCase()}
                    </span>
                    <span className="font-medium text-gray-800">{selectedUser.username}</span>
                    <span className="text-gray-400">—</span>
                    <span className="text-gray-500">{selectedUser.full_name || selectedUser.email}</span>
                  </span>
                ) : (
                  <span className="text-gray-400">Choose a user...</span>
                )}
                <ChevronDown size={16} className={`text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {dropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-80 overflow-hidden">
                  <div className="p-2 border-b border-gray-100">
                    <div className="relative">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search by username, name, or email... (F4)"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleCrmSearchKeyDown}
                        className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-indigo-400"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div ref={crmDropdownRef} className="max-h-60 overflow-y-auto">
                    {usersLoading ? (
                      <div className="flex items-center justify-center py-6"><Loader2 size={20} className="animate-spin text-indigo-500" /></div>
                    ) : filteredUsers.length === 0 ? (
                      <div className="text-center py-6 text-sm text-gray-400">No users found</div>
                    ) : (
                      filteredUsers.map((u, idx) => (
                        <button
                          key={u.id}
                          data-crm-user
                          onClick={() => selectUser(u.id)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-indigo-50 transition-colors ${
                            idx === highlightedIdx ? 'bg-indigo-100' : u.id === selectedUserId ? 'bg-indigo-50' : ''
                          }`}
                        >
                          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                            u.is_active ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-400'
                          }`}>
                            {u.username.charAt(0).toUpperCase()}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-800 truncate">{u.username}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                u.role === 'master_admin' ? 'bg-purple-100 text-purple-700' :
                                u.role === 'user' ? 'bg-blue-100 text-blue-700' :
                                u.role === 'support' ? 'bg-amber-100 text-amber-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>{u.role}</span>
                            </div>
                            <span className="text-xs text-gray-400 truncate block">{u.full_name || u.email}</span>
                          </div>
                          <span className="text-xs font-mono text-gray-500">{fmtMoney(u.balance)}</span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {selectedUserId && (
            <button
              onClick={() => loadCRM(selectedUserId)}
              disabled={loading}
              className="mt-5 flex items-center gap-1.5 px-3.5 py-2.5 bg-indigo-50 text-indigo-600 rounded-lg text-sm font-medium hover:bg-indigo-100 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 size={32} className="animate-spin text-indigo-500 mx-auto mb-3" />
            <p className="text-sm text-gray-500">Loading user data from database...</p>
          </div>
        </div>
      )}

      {/* No User Selected */}
      {!selectedUserId && !loading && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <User size={48} className="text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-400">Select a user from the dropdown to view their CRM dashboard</p>
          </div>
        </div>
      )}

      {/* CRM Data */}
      {crmData && !loading && (
        <div className="space-y-6">

          {/* ═══ Row 1: Profile + Dates + Status ═══ */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            {/* Profile Card */}
            <SectionCard title="User Profile" icon={User} className="lg:col-span-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0">
                <div>
                  <InfoRow label="Username" value={p?.username} mono />
                  <InfoRow label="Full Name" value={p?.full_name} />
                  <InfoRow label="Email" value={p?.email} />
                  <InfoRow label="Mobile" value={p?.mobile} />
                  <InfoRow label="Company" value={p?.company_name} />
                  <InfoRow label="Role" value={
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      p?.role === 'master_admin' ? 'bg-purple-100 text-purple-700' :
                      p?.role === 'user' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>{p?.role}</span>
                  } />
                </div>
                <div>
                  <InfoRow label="Status" value={<StatusBadge active={p?.is_active} />} />
                  <InfoRow label="TPS Limit" value={p?.tps_limit} />
                  <InfoRow label="Balance Enforced" value={<StatusBadge active={p?.balance_enforced} trueLabel="Yes" falseLabel="No" />} />
                  <InfoRow label="API Key" value={
                    p?.api_key ? (
                      <span className="flex items-center gap-1">
                        <span className="font-mono text-[10px]">{showApiKey ? p.api_key : '••••••••••••'}</span>
                        <button onClick={() => setShowApiKey(!showApiKey)} className="text-gray-400 hover:text-indigo-500">
                          {showApiKey ? <EyeOff size={12} /> : <Eye size={12} />}
                        </button>
                        <button onClick={() => { navigator.clipboard.writeText(p.api_key); showToast('API key copied'); }} className="text-gray-400 hover:text-indigo-500">
                          <Copy size={12} />
                        </button>
                      </span>
                    ) : null
                  } />
                  <InfoRow label="DLR URL" value={p?.dlr_url} mono />
                  <InfoRow label="System Type" value={p?.system_type} />
                </div>
              </div>
            </SectionCard>

            {/* Dates Card */}
            <SectionCard title="Important Dates" icon={Calendar}>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-indigo-50 rounded-lg">
                  <Calendar size={18} className="text-indigo-500" />
                  <div>
                    <p className="text-[10px] text-indigo-400 uppercase font-semibold">Created</p>
                    <p className="text-sm font-medium text-indigo-700">{fmtDate(p?.created_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Clock size={18} className="text-gray-500" />
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase font-semibold">Last Login</p>
                    <p className="text-sm font-medium text-gray-700">{fmtDateTime(p?.last_login)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <RefreshCw size={18} className="text-gray-500" />
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase font-semibold">Last Updated</p>
                    <p className="text-sm font-medium text-gray-700">{fmtDateTime(p?.updated_at)}</p>
                  </div>
                </div>
                {plan?.assigned && plan?.expires_at && (
                  <div className={`flex items-center gap-3 p-3 rounded-lg ${
                    new Date(plan.expires_at) < new Date() ? 'bg-red-50' : 'bg-emerald-50'
                  }`}>
                    <AlertTriangle size={18} className={new Date(plan.expires_at) < new Date() ? 'text-red-500' : 'text-emerald-500'} />
                    <div>
                      <p className={`text-[10px] uppercase font-semibold ${new Date(plan.expires_at) < new Date() ? 'text-red-400' : 'text-emerald-400'}`}>Plan Expiry</p>
                      <p className={`text-sm font-medium ${new Date(plan.expires_at) < new Date() ? 'text-red-700' : 'text-emerald-700'}`}>{fmtDate(plan.expires_at)}</p>
                    </div>
                  </div>
                )}
              </div>
            </SectionCard>
          </div>

          {/* ═══ Row 2: Plan + Rate + Balance ═══ */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            {/* Plan */}
            <SectionCard title="User Plan" icon={Package}
              action={!plan?.assigned && (
                <button onClick={() => setShowPlanModal(true)} className="text-xs px-3 py-1.5 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors font-medium">
                  Assign Plan
                </button>
              )}
            >
              {plan?.assigned ? (
                <div className="space-y-1">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-base font-bold text-indigo-600">{plan.plan_name}</span>
                    <StatusBadge active={plan.status === 'ACTIVE'} trueLabel={plan.status} falseLabel={plan.status} />
                  </div>
                  <InfoRow label="Channel" value={plan.plan_channel} />
                  <InfoRow label="Submit Type" value={plan.submit_type} />
                  <InfoRow label="SMS Limit" value={fmtNumber(plan.sms_limit)} />
                  <InfoRow label="Remaining SMS" value={fmtNumber(plan.remaining_sms)} />
                  <InfoRow label="Daily Limit" value={fmtNumber(plan.daily_limit)} />
                  <InfoRow label="Default TPS" value={plan.default_tps} />
                  <InfoRow label="Validity" value={`${plan.validity_days} days`} />
                  <InfoRow label="Rate/SMS" value={fmtRate(plan.price_per_sms)} />
                  <InfoRow label="Start Date" value={fmtDate(plan.start_date)} />
                  <InfoRow label="End Date" value={fmtDate(plan.end_date)} />
                  <InfoRow label="Payment" value={plan.payment_status} />
                  <div className="pt-2 mt-2 border-t border-gray-100">
                    <button onClick={() => setShowPlanModal(true)} className="text-xs text-indigo-500 hover:text-indigo-700 font-medium">
                      Change Plan
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <Package size={32} className="text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-400 mb-3">No plan assigned</p>
                  <button onClick={() => setShowPlanModal(true)} className="px-4 py-2 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600 transition-colors">
                    Assign Plan
                  </button>
                </div>
              )}
            </SectionCard>

            {/* Rate */}
            <SectionCard title="User Rate" icon={BadgeDollarSign}
              action={
                <button onClick={() => setShowRateModal(true)} className="text-xs px-3 py-1.5 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors font-medium">
                  {rates?.assigned ? 'Update Rate' : 'Assign Rate'}
                </button>
              }
            >
              {rates?.assigned && rates.rates?.length > 0 ? (
                <div className="space-y-3">
                  {rates.rates.map((r, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-600">{r.route_type}</span>
                        <span className="text-sm font-bold text-indigo-600">{fmtRate(r.price_per_sms, r.currency)}/SMS</span>
                      </div>
                      <p className="text-[10px] text-gray-400">Since {fmtDate(r.created_at)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <BadgeDollarSign size={32} className="text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-400 mb-3">No rate assigned</p>
                  <button onClick={() => setShowRateModal(true)} className="px-4 py-2 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600 transition-colors">
                    Assign Rate
                  </button>
                </div>
              )}
            </SectionCard>

            {/* Balance */}
            <SectionCard title="Balance" icon={Wallet}
              action={
                <button onClick={() => setShowBalanceModal(true)} className="text-xs px-3 py-1.5 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors font-medium">
                  Credit / Debit
                </button>
              }
            >
              <div className="text-center mb-4">
                <p className="text-3xl font-bold text-gray-900">{fmtMoney(bal?.balance, bal?.currency)}</p>
                <p className="text-xs text-gray-400 mt-1">Available Balance</p>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="p-2.5 bg-emerald-50 rounded-lg text-center">
                  <TrendingUp size={14} className="text-emerald-500 mx-auto mb-1" />
                  <p className="text-xs font-bold text-emerald-700">{fmtMoney(bal?.total_recharged, bal?.currency)}</p>
                  <p className="text-[10px] text-emerald-500">Total Recharged</p>
                </div>
                <div className="p-2.5 bg-red-50 rounded-lg text-center">
                  <TrendingDown size={14} className="text-red-500 mx-auto mb-1" />
                  <p className="text-xs font-bold text-red-700">{fmtMoney(bal?.total_spent, bal?.currency)}</p>
                  <p className="text-[10px] text-red-500">Total Spent</p>
                </div>
              </div>
              {bal?.credit_enabled && (
                <div className="p-2.5 bg-amber-50 rounded-lg mb-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-amber-600">Credit Limit</span>
                    <span className="text-xs font-bold text-amber-700">{fmtMoney(bal.credit_limit, bal.currency)}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-amber-600">Credit Used</span>
                    <span className="text-xs font-bold text-amber-700">{fmtMoney(bal.credit_used, bal.currency)}</span>
                  </div>
                </div>
              )}
              {/* Last 3 Recharges */}
              {recharges && recharges.length > 0 && (
                <div className="border-t border-gray-100 pt-3">
                  <p className="text-xs font-semibold text-gray-500 mb-2">Last {recharges.length} Transactions</p>
                  {recharges.map((r, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                      <div className="flex items-center gap-2">
                        {r.transaction_type === 'recharge' ? (
                          <ArrowUpCircle size={14} className="text-emerald-500" />
                        ) : (
                          <ArrowDownCircle size={14} className="text-red-500" />
                        )}
                        <div>
                          <p className="text-xs font-medium text-gray-700">{r.transaction_type}</p>
                          <p className="text-[10px] text-gray-400">{fmtDate(r.created_at)}</p>
                        </div>
                      </div>
                      <span className={`text-xs font-bold ${r.transaction_type === 'recharge' ? 'text-emerald-600' : 'text-red-600'}`}>
                        {r.transaction_type === 'recharge' ? '+' : '-'}{fmtMoney(r.amount, bal?.currency)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>

          {/* ═══ Row 2.5: Mapped DLT Entities ═══ */}
          <SectionCard title="Mapped DLT Entities" icon={Building2}>
            {entities && entities.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 text-gray-500 font-medium">Entity Name</th>
                      <th className="text-left py-2 text-gray-500 font-medium">Entity ID (PE ID)</th>
                      <th className="text-left py-2 text-gray-500 font-medium">Status</th>
                      <th className="text-left py-2 text-gray-500 font-medium">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entities.map((entity, i) => (
                      <tr key={entity.id || i} className="border-b border-gray-50 last:border-0">
                        <td className="py-2 font-medium text-gray-800">{entity.entity_name}</td>
                        <td className="py-2 font-mono text-gray-600">{entity.entity_id || '—'}</td>
                        <td className="py-2">
                          <StatusBadge active={entity.is_active} />
                        </td>
                        <td className="py-2 text-gray-500">{fmtDate(entity.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-gray-400 mt-2">{entities.length} entit{entities.length === 1 ? 'y' : 'ies'} mapped</p>
              </div>
            ) : (
              <div className="text-center py-6">
                <Building2 size={32} className="text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No DLT entities mapped to this user</p>
              </div>
            )}
          </SectionCard>

          {/* ═══ Row 2.6: IP Whitelist Status ═══ */}
          <SectionCard title="IP Whitelist Status" icon={Shield}>
            {ipWhitelist ? (
              <div>
                {/* Summary badges */}
                <div className="flex items-center gap-3 mb-3">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <Check size={12} /> {ipWhitelist.approved_count || 0} Approved
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                    <Clock size={12} /> {ipWhitelist.pending_count || 0} Pending
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
                    <X size={12} /> {ipWhitelist.rejected_count || 0} Rejected
                  </span>
                </div>

                {/* Approved IPs */}
                {ipWhitelist.approved?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5">Approved IPs</p>
                    <div className="space-y-1">
                      {ipWhitelist.approved.map((ip, i) => (
                        <div key={i} className="flex items-center justify-between py-1.5 px-2 bg-emerald-50/50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Globe size={12} className="text-emerald-500" />
                            <span className="text-xs font-mono text-gray-700">{ip.ip_address}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{ip.ip_type}</span>
                          </div>
                          <StatusBadge active={ip.is_active} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pending Requests */}
                {ipWhitelist.pending?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[10px] font-bold text-amber-500 uppercase mb-1.5">Pending Requests</p>
                    <div className="space-y-1">
                      {ipWhitelist.pending.map((r, i) => (
                        <div key={i} className="flex items-center justify-between py-1.5 px-2 bg-amber-50/50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Clock size={12} className="text-amber-500" />
                            <span className="text-xs font-mono text-gray-700">{r.ip_address}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{r.ip_type}</span>
                          </div>
                          <span className="text-[10px] text-gray-400">{fmtDate(r.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rejected Requests */}
                {ipWhitelist.rejected?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold text-red-500 uppercase mb-1.5">Rejected Requests</p>
                    <div className="space-y-1">
                      {ipWhitelist.rejected.map((r, i) => (
                        <div key={i} className="flex items-center justify-between py-1.5 px-2 bg-red-50/50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <X size={12} className="text-red-500" />
                            <span className="text-xs font-mono text-gray-700">{r.ip_address}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{r.ip_type}</span>
                          </div>
                          <span className="text-[10px] text-gray-400">{r.remarks || fmtDate(r.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!ipWhitelist.approved?.length && !ipWhitelist.pending?.length && !ipWhitelist.rejected?.length && (
                  <p className="text-xs text-gray-400 text-center py-4">No IP whitelist records</p>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-400 text-center py-4">No IP whitelist data</p>
            )}
          </SectionCard>

          {/* ═══ Row 2.7: Block Keywords + Block Mobiles ═══ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

            {/* Block Keywords */}
            <SectionCard title="Block Keywords" icon={ShieldAlert} action={
              <button onClick={() => { setNewKwText(''); setShowAddKwModal(true); }} className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 border border-purple-200">
                <Plus size={12} /> Add
              </button>
            }>
              {/* Search */}
              <div className="relative mb-3">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={kwSearch} onChange={e => setKwSearch(e.target.value)} placeholder="Search keywords..." className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-300" />
              </div>
              {blockKeywords && blockKeywords.total > 0 ? (
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs text-gray-500">
                      <strong className="text-gray-700">{blockKeywords.total}</strong> total
                      {blockKeywords.user_count > 0 && <> · <span className="text-purple-600">{blockKeywords.user_count} user</span></>}
                      {blockKeywords.global_count > 0 && <> · <span className="text-blue-600">{blockKeywords.global_count} global</span></>}
                    </span>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {blockKeywords.keywords
                      .filter(kw => !kwSearch || kw.keyword.toLowerCase().includes(kwSearch.toLowerCase()))
                      .map((kw, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 group">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                            kw.scope === 'USER' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-blue-50 text-blue-700 border-blue-200'
                          }`}>{kw.keyword}</span>
                          <span className="text-[9px] text-gray-400">{kw.scope}</span>
                        </div>
                        {kw.scope === 'USER' && (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => { setEditKw(kw); setNewKwText(kw.keyword); }} className="p-1 text-gray-400 hover:text-blue-600" title="Edit"><Edit3 size={12} /></button>
                            <button onClick={() => handleDeleteKeyword(kw.id)} className="p-1 text-gray-400 hover:text-red-600" title="Deactivate"><Trash2 size={12} /></button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <ShieldAlert size={24} className="text-gray-200 mx-auto mb-1" />
                  <p className="text-xs text-gray-400">No block keywords</p>
                </div>
              )}
            </SectionCard>

            {/* Block Mobiles */}
            <SectionCard title="Blocked Mobile Numbers" icon={Phone} action={
              <button onClick={() => { setNewMbText(''); setNewMbReason(''); setShowAddMbModal(true); }} className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 border border-red-200">
                <Plus size={12} /> Add
              </button>
            }>
              {/* Search */}
              <div className="relative mb-3">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={mbSearch} onChange={e => setMbSearch(e.target.value)} placeholder="Search mobiles..." className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-300" />
              </div>
              {blockMobiles && blockMobiles.total > 0 ? (
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs text-gray-500">
                      <strong className="text-gray-700">{blockMobiles.total}</strong> total
                      {blockMobiles.user_count > 0 && <> · <span className="text-purple-600">{blockMobiles.user_count} user</span></>}
                      {blockMobiles.global_count > 0 && <> · <span className="text-blue-600">{blockMobiles.global_count} global</span></>}
                    </span>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {blockMobiles.mobiles
                      .filter(m => !mbSearch || m.mobile_number.includes(mbSearch))
                      .map((m, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 group">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold border ${
                            m.scope === 'USER' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-blue-50 text-blue-700 border-blue-200'
                          }`}>{m.mobile_number}</span>
                          {m.reason && <span className="text-[9px] text-gray-400 truncate max-w-[80px]" title={m.reason}>{m.reason}</span>}
                        </div>
                        {m.scope === 'USER' && (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => { setEditMb(m); setNewMbText(m.mobile_number); setNewMbReason(m.reason || ''); }} className="p-1 text-gray-400 hover:text-blue-600" title="Edit"><Edit3 size={12} /></button>
                            <button onClick={() => handleDeleteMobile(m.id)} className="p-1 text-gray-400 hover:text-red-600" title="Deactivate"><Trash2 size={12} /></button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <Phone size={24} className="text-gray-200 mx-auto mb-1" />
                  <p className="text-xs text-gray-400">No blocked mobile numbers</p>
                </div>
              )}
            </SectionCard>

          </div>

          {/* ═══ Row 2.8: Masking Summary ═══ */}
          {crmData?.masking && (
            <SectionCard title="Content Masking" icon={Fingerprint}>
              <div className="flex items-center gap-2 flex-wrap">
                {crmData.masking.masking_enabled ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200">
                    <Fingerprint size={12} /> Masking ON
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-400 border border-gray-200">
                    <Fingerprint size={12} /> Masking OFF
                  </span>
                )}
                {crmData.masking.masking_enabled && crmData.masking.type_names?.length > 0 && (
                  <>
                    {crmData.masking.type_names.map((t, i) => (
                      <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                        {t}
                      </span>
                    ))}
                    {crmData.masking.template_count > 0 && (
                      <span className="text-[10px] text-gray-500">{crmData.masking.template_count} template(s) masked</span>
                    )}
                    {crmData.masking.mobile_mask_pattern && crmData.masking.type_names?.includes('Mobile') && (
                      <span className="text-[10px] text-gray-500">Pattern: {crmData.masking.mobile_mask_pattern}</span>
                    )}
                  </>
                )}
              </div>
            </SectionCard>
          )}

          {/* ═══ Row 3: SMPP Connection + Login Alerts ═══ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

            {/* SMPP Connection */}
            <SectionCard title="SMPP Connection" icon={Activity}>
              <div className="grid grid-cols-2 gap-x-6 gap-y-0">
                <InfoRow label="System ID" value={p?.smpp_system_id} mono />
                <InfoRow label="Bind Type" value={p?.smpp_bind_type} />
                <InfoRow label="Status" value={
                  <StatusBadge active={p?.smpp_is_connected} trueLabel="Connected" falseLabel="Disconnected" />
                } />
                <InfoRow label="Connection Status" value={p?.smpp_connection_status} />
                <InfoRow label="Remote IP" value={p?.smpp_remote_ip} mono />
                <InfoRow label="Connection Count" value={p?.smpp_connection_count} />
                <InfoRow label="Connected At" value={fmtDateTime(p?.smpp_connected_at)} />
                <InfoRow label="Disconnected At" value={fmtDateTime(p?.smpp_disconnected_at)} />
                <InfoRow label="Last Activity" value={fmtDateTime(p?.smpp_last_activity)} />
                <InfoRow label="Last Test" value={fmtDateTime(p?.smpp_last_test_at)} />
                <InfoRow label="Test Result" value={p?.smpp_last_test_result} />
                <InfoRow label="Test Count" value={p?.smpp_test_count} />
              </div>
            </SectionCard>

            {/* Login Alerts */}
            <SectionCard title="Login Alert Management" icon={ShieldAlert}>
              <div className="space-y-3">
                {/* Status badges */}
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-semibold text-gray-500">Login Alerts:</span>
                    {alerts?.enabled ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <Bell size={12} /> Enabled
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-500 border border-gray-200">
                        <BellOff size={12} /> Disabled
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-semibold text-gray-500">OTP Verification:</span>
                    {alerts?.otp_login_enabled ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200">
                        <KeyRound size={12} /> Enabled
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-400 border border-gray-200">
                        <KeyRound size={12} /> Disabled
                      </span>
                    )}
                  </div>
                </div>

                {/* Emails list */}
                {alerts?.emails?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Email Contacts</p>
                    <div className="flex flex-wrap gap-1.5">
                      {alerts.emails.map((e, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
                          <Mail size={10} /> {e}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mobiles list */}
                {alerts?.mobiles?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">SMS / OTP Contacts</p>
                    <div className="flex flex-wrap gap-1.5">
                      {alerts.mobiles.map((m, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono font-medium bg-green-50 text-green-700 border border-green-200">
                          <Phone size={10} /> {m}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* All contacts detail */}
                {alerts?.contacts?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">All Contacts</p>
                    {alerts.contacts.map((c, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                        <div className="flex items-center gap-2">
                          {c.contact_type === 'email' ? <Mail size={12} className="text-blue-500" /> : <Phone size={12} className="text-green-500" />}
                          <span className="text-xs text-gray-700">{c.contact_value}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-gray-400 uppercase">{c.contact_type}</span>
                          <StatusBadge active={c.is_active} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Recent logs */}
                {alerts?.logs?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Recent Alert Logs</p>
                    <div className="max-h-40 overflow-y-auto">
                      {alerts.logs.map((l, i) => (
                        <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                          <div className="flex items-center gap-2">
                            <span className={`w-1.5 h-1.5 rounded-full ${l.status === 'sent' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                            <span className="text-xs text-gray-600">{l.alert_type}</span>
                            {l.ip_address && <span className="text-[10px] font-mono text-gray-400">{l.ip_address}</span>}
                          </div>
                          <span className="text-[10px] text-gray-400">{fmtDateTime(l.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(!alerts?.contacts || alerts.contacts.length === 0) && (!alerts?.emails || alerts.emails.length === 0) && (!alerts?.logs || alerts.logs.length === 0) && (
                  <p className="text-xs text-gray-400 text-center py-4">No login alert configuration</p>
                )}
              </div>
            </SectionCard>
          </div>

          {/* ═══ Row 4: IP History + SMS Usage ═══ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

            {/* IP History */}
            <SectionCard title="IP History" icon={Globe}>
              {ipHistory && ipHistory.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-2 text-gray-500 font-medium">IP Address</th>
                        <th className="text-left py-2 text-gray-500 font-medium">Location</th>
                        <th className="text-center py-2 text-gray-500 font-medium">Logins</th>
                        <th className="text-left py-2 text-gray-500 font-medium">Last Seen</th>
                        <th className="text-center py-2 text-gray-500 font-medium">Trusted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ipHistory.map((ip, i) => (
                        <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-2 font-mono text-gray-700">{ip.ip_address}</td>
                          <td className="py-2 text-gray-600">{ip.location || '—'}</td>
                          <td className="py-2 text-center text-gray-700 font-medium">{ip.login_count}</td>
                          <td className="py-2 text-gray-500">{fmtDateTime(ip.last_seen_at)}</td>
                          <td className="py-2 text-center">
                            {ip.is_trusted ? (
                              <Shield size={13} className="text-emerald-500 mx-auto" />
                            ) : (
                              <AlertTriangle size={13} className="text-amber-500 mx-auto" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-gray-400 text-center py-6">No IP history available</p>
              )}
            </SectionCard>

            {/* SMS Usage */}
            <SectionCard title="SMS Usage (Last 5 Months)" icon={BarChart3}>
              <SMSUsageChart data={smsUsage} />
            </SectionCard>
          </div>
        </div>
      )}

      {/* ═══ Modals ═══ */}

      {/* Assign Plan Modal */}
      {showPlanModal && (
        <Modal title="Assign Plan" onClose={() => setShowPlanModal(false)}>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {availablePlans && availablePlans.length > 0 ? (
              availablePlans.map(pl => (
                <button
                  key={pl.id}
                  onClick={() => handleAssignPlan(pl.id)}
                  disabled={actionLoading}
                  className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors disabled:opacity-50"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-bold text-gray-800">{pl.plan_name}</span>
                    <span className="text-sm font-bold text-indigo-600">{fmtRate(pl.price_per_sms)}/SMS</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>Channel: {pl.plan_channel}</span>
                    <span>SMS: {fmtNumber(pl.sms_limit)}</span>
                    <span>TPS: {pl.default_tps}</span>
                    <span>{pl.validity_days} days</span>
                  </div>
                  {pl.description && <p className="text-xs text-gray-400 mt-1">{pl.description}</p>}
                </button>
              ))
            ) : (
              <p className="text-sm text-gray-400 text-center py-6">No active plans available</p>
            )}
          </div>
        </Modal>
      )}

      {/* Assign Rate Modal */}
      {showRateModal && (
        <RateModal
          onClose={() => setShowRateModal(false)}
          onSubmit={handleAssignRate}
          loading={actionLoading}
        />
      )}

      {/* Credit/Debit Modal */}
      {showBalanceModal && (
        <BalanceModal
          onClose={() => setShowBalanceModal(false)}
          onSubmit={handleCreditDebit}
          loading={actionLoading}
          currentBalance={bal?.balance}
          currency={bal?.currency}
        />
      )}

      {/* Add Keyword Modal */}
      {showAddKwModal && (
        <Modal title="Add Block Keyword" onClose={() => setShowAddKwModal(false)}>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Keywords (comma or newline separated)</label>
              <textarea value={newKwText} onChange={e => setNewKwText(e.target.value)} rows={3}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-purple-400"
                placeholder="e.g. lottery, gambling, casino" autoFocus />
            </div>
            <p className="text-[10px] text-gray-400">Previously deleted keywords will be automatically reactivated.</p>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setShowAddKwModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={handleAddKeyword} disabled={!newKwText.trim() || actionLoading}
                className="px-4 py-2 bg-purple-500 text-white text-sm font-medium rounded-lg hover:bg-purple-600 disabled:opacity-50">
                {actionLoading ? <Loader2 size={16} className="animate-spin" /> : 'Add Keywords'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Keyword Modal */}
      {editKw && (
        <Modal title="Edit Block Keyword" onClose={() => { setEditKw(null); setNewKwText(''); }}>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Keyword</label>
              <input value={newKwText} onChange={e => setNewKwText(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-purple-400" autoFocus />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => { setEditKw(null); setNewKwText(''); }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={handleUpdateKeyword} disabled={!newKwText.trim() || actionLoading}
                className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50">
                {actionLoading ? <Loader2 size={16} className="animate-spin" /> : 'Update'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Add Mobile Block Modal */}
      {showAddMbModal && (
        <Modal title="Block Mobile Number" onClose={() => setShowAddMbModal(false)}>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Mobile Numbers (comma or newline separated)</label>
              <textarea value={newMbText} onChange={e => setNewMbText(e.target.value)} rows={3}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:border-red-400"
                placeholder="e.g. 9876543210, 9123456789" autoFocus />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Reason (optional)</label>
              <input value={newMbReason} onChange={e => setNewMbReason(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-red-400"
                placeholder="e.g. DND, spam complaint" />
            </div>
            <p className="text-[10px] text-gray-400">Previously unblocked numbers will be automatically reactivated.</p>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setShowAddMbModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={handleAddMobile} disabled={!newMbText.trim() || actionLoading}
                className="px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 disabled:opacity-50">
                {actionLoading ? <Loader2 size={16} className="animate-spin" /> : 'Block Mobile(s)'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Mobile Block Modal */}
      {editMb && (
        <Modal title="Edit Blocked Mobile" onClose={() => { setEditMb(null); setNewMbText(''); setNewMbReason(''); }}>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Mobile Number</label>
              <input value={newMbText} onChange={e => setNewMbText(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:border-red-400" autoFocus />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Reason</label>
              <input value={newMbReason} onChange={e => setNewMbReason(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-red-400" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => { setEditMb(null); setNewMbText(''); setNewMbReason(''); }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={handleUpdateMobile} disabled={!newMbText.trim() || actionLoading}
                className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50">
                {actionLoading ? <Loader2 size={16} className="animate-spin" /> : 'Update'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── Modal Components ────────────────────────────────────────────────────────

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <h3 className="text-base font-bold text-gray-800">{title}</h3>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
          <X size={18} className="text-gray-400" />
        </button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

const RateModal = ({ onClose, onSubmit, loading }) => {
  const [price, setPrice] = useState('');
  const [routeType, setRouteType] = useState('default');
  const [currency, setCurrency] = useState('INR');

  return (
    <Modal title="Assign Rate" onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Price per SMS</label>
          <input
            type="number" step="0.0001" min="0" value={price} onChange={e => setPrice(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400"
            placeholder="e.g. 0.2500"
            autoFocus
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Route Type</label>
          <select value={routeType} onChange={e => setRouteType(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400">
            <option value="default">Default</option>
            <option value="promotional">Promotional</option>
            <option value="transactional">Transactional</option>
            <option value="otp">OTP</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Currency</label>
          <select value={currency} onChange={e => setCurrency(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400">
            <option value="INR">INR (₹)</option>
            <option value="USD">USD ($)</option>
          </select>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
          <button
            onClick={() => price && onSubmit(price, routeType, currency)}
            disabled={!price || loading}
            className="px-4 py-2 bg-indigo-500 text-white text-sm font-medium rounded-lg hover:bg-indigo-600 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : 'Assign Rate'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const BalanceModal = ({ onClose, onSubmit, loading, currentBalance, currency }) => {
  const [type, setType] = useState('credit');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  return (
    <Modal title="Credit / Debit Balance" onClose={onClose}>
      <div className="space-y-4">
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500">Current Balance</p>
          <p className="text-xl font-bold text-gray-800">{fmtMoney(currentBalance, currency)}</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setType('credit')}
              className={`py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                type === 'credit' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <ArrowUpCircle size={16} /> Credit
            </button>
            <button
              onClick={() => setType('debit')}
              className={`py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                type === 'debit' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <ArrowDownCircle size={16} /> Debit
            </button>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Amount</label>
          <input
            type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400"
            placeholder="Enter amount"
            autoFocus
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Reason</label>
          <input
            type="text" value={reason} onChange={e => setReason(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400"
            placeholder="e.g. Monthly recharge, Refund, etc."
          />
        </div>
        {amount && (
          <div className={`p-3 rounded-lg text-sm ${type === 'credit' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
            New balance will be: <strong>{fmtMoney(
              type === 'credit' ? (currentBalance || 0) + parseFloat(amount || 0) : (currentBalance || 0) - parseFloat(amount || 0),
              currency
            )}</strong>
          </div>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
          <button
            onClick={() => amount && reason && onSubmit(type, amount, reason)}
            disabled={!amount || !reason || loading}
            className={`px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
              type === 'credit' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'
            }`}
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : `${type === 'credit' ? 'Credit' : 'Debit'} Balance`}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default UserCRMDashboard;
